import i18n from "../../../i18n";

function sq(key, options) {
  return i18n.t(`salesQuotation.${key}`, options);
}

function ChoiceCard({ selected, title, description, onClick, disabled = false, badge }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      className={`mb-2.5 w-full rounded-[12px] border px-4 py-3.5 text-left transition ${
        disabled
          ? "cursor-not-allowed border-[#e4e9f2] bg-[#f7f9fc] opacity-80"
          : selected
            ? "border-[#243c93] bg-[#f7f9ff]"
            : "border-[#d1d9e8] bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p
              className={`text-[14px] font-semibold leading-5 ${
                disabled ? "text-[#8ea0bd]" : "text-[#23324c]"
              }`}
            >
              {title}
            </p>
            {badge ? (
              <span className="rounded-[6px] bg-[#eef2f8] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#6f82a1]">
                {badge}
              </span>
            ) : null}
          </div>
          {description ? (
            <p
              className={`mt-1 text-[13px] font-normal leading-5 ${
                disabled ? "text-[#a0afc4]" : "text-[#6f82a1]"
              }`}
            >
              {description}
            </p>
          ) : null}
        </div>
        <span
          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] ${
            disabled
              ? "border-[#d8e0ed] text-[#c5cedd]"
              : selected
                ? "border-[#243c93] bg-[#243c93] text-white"
                : "border-[#c5cedd] text-transparent"
          }`}
          aria-hidden
        >
          {selected && !disabled ? "✓" : ""}
        </span>
      </div>
    </button>
  );
}

function Field({ label, required = false, error, hint, children }) {
  return (
    <div className="mb-4">
      <div className="mb-1.5 flex items-center gap-1.5">
        <label className="text-[12px] font-semibold text-[#51657f]">{label}</label>
        {required ? <span className="text-[12px] text-[#c74b40]">*</span> : null}
      </div>
      {children}
      {error ? (
        <p className="mt-1.5 text-[11px] font-medium text-[#c74b40]">{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-[11px] font-normal leading-4 text-[#8ea0bd]">{hint}</p>
      ) : null}
    </div>
  );
}

function inputClass(hasError) {
  return `min-h-[44px] w-full rounded-[12px] border bg-white px-4 text-[13px] font-normal text-[#2d394c] outline-hidden transition placeholder:text-[#96a5bb] ${
    hasError ? "border-[#e4a5a0]" : "border-[#d1d9e8] focus:border-[#95addd]"
  }`;
}

const TYPE_OPTIONS = [
  { value: "SERVICE", titleKey: "typeService", hintKey: "mobileTypeServiceHint" },
  { value: "SITC", titleKey: "typeSitc", hintKey: "mobileTypeSitcHint" },
  { value: "Supply", titleKey: "typeSupply", hintKey: "mobileTypeSupplyHint" },
];

/**
 * Mobile-only create wizard: Type → Customer → Create.
 * Footer is outside the scroll region so Cancel/Next/Create never cover fields.
 */
export function QuotationCreateMobileWizard({
  step,
  onStepChange,
  form,
  errors,
  patchForm,
  onQuotationTypeChange,
  isServiceType,
  showServiceRoles,
  serviceExecutiveLabel,
  serviceManagerLabel,
  complaintSelect,
  customerSelect,
  locationSelect,
  contactSelect,
  salesExecutiveSelect,
  salesSupportSelect,
  submitError,
  apiError,
  onClearErrors,
  onCancel,
  onCreate,
  isCreateDisabled,
  submitting,
  localizedFieldProps,
}) {
  const totalSteps = 2;
  const activeStep = Math.min(Math.max(Number(step) || 0, 0), totalSteps - 1);
  const stepTitles = [
    sq("mobileStepTypeTitle"),
    sq("mobileStepCustomerTitle"),
  ];
  const isLastStep = activeStep >= totalSteps - 1;

  function canProceedFromStep(currentStep) {
    if (currentStep === 0) {
      if (!form.refId?.trim() || !form.type || !form.expiry) return false;
      if (form.type === "SERVICE" && !String(form.complaintId || "").trim()) return false;
      return true;
    }
    if (currentStep === 1) {
      return Boolean(form.customer && form.location);
    }
    return true;
  }

  function handleNext() {
    if (!canProceedFromStep(activeStep)) return;
    if (activeStep < totalSteps - 1) onStepChange(activeStep + 1);
  }

  function handleBack() {
    if (activeStep > 0) {
      onStepChange(activeStep - 1);
      return;
    }
    onCancel?.();
  }

  function handlePrimary() {
    if (!isLastStep) {
      handleNext();
      return;
    }
    onCreate?.();
  }

  const primaryDisabled = isLastStep
    ? isCreateDisabled
    : !canProceedFromStep(activeStep);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Only this region scrolls — footer stays below it */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[20px] font-semibold tracking-[-0.02em] text-[#23324c]">
              {sq("createFormTitle")}
            </h2>
            <p className="mt-1 text-[13px] font-normal text-[#6f82a1]">
              {sq("mobileStepOf", { current: activeStep + 1, total: totalSteps })}
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-[14px] bg-[#eef2f8] px-3 py-1.5 text-[12px] font-semibold text-[#586c8f]">
            <span className="h-2 w-2 rounded-full bg-current opacity-95" />
            {sq("statusDraft")}
          </span>
        </div>

        {(submitError || apiError) && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-[12px] border border-[#f4cfcb] bg-[#fff6f5] px-4 py-3 text-[#b04d43]">
            <p className="text-[12px] font-normal leading-5">{submitError || apiError}</p>
            <button
              type="button"
              onClick={onClearErrors}
              className="shrink-0 text-[12px] font-semibold text-[#2944a8]"
            >
              {sq("retry")}
            </button>
          </div>
        )}

        <div className="mb-3 flex gap-1.5">
          {Array.from({ length: totalSteps }).map((_, index) => (
            <div
              key={`step-bar-${index}`}
              className={`h-1 flex-1 rounded-full ${
                index <= activeStep ? "bg-[#243c93]" : "bg-[#dde5f1]"
              }`}
            />
          ))}
        </div>

        <h3 className="mb-4 text-[15px] font-semibold text-[#2d394c]">{stepTitles[activeStep]}</h3>

        {activeStep === 0 && (
          <div>
            <div className="mb-4">
              {TYPE_OPTIONS.map((option) => (
                <ChoiceCard
                  key={option.value}
                  selected={form.type === option.value}
                  title={sq(option.titleKey)}
                  description={sq(option.hintKey)}
                  onClick={() => onQuotationTypeChange(option.value)}
                />
              ))}
              {errors.type ? (
                <p className="mt-1 text-[11px] font-medium text-[#c74b40]">{errors.type}</p>
              ) : null}
            </div>

            <Field label={sq("refId")} required error={errors.refId}>
              <input
                value={form.refId}
                onChange={(event) => patchForm("refId", event.target.value)}
                className={inputClass(Boolean(errors.refId))}
                placeholder={sq("refId")}
                {...localizedFieldProps()}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label={sq("tenderDueDate")} error={errors.tenderDueDate}>
                <input
                  type="date"
                  value={form.tenderDueDate}
                  onChange={(event) => patchForm("tenderDueDate", event.target.value)}
                  className={inputClass(Boolean(errors.tenderDueDate))}
                  {...localizedFieldProps()}
                />
              </Field>
              <Field label={sq("quotationExpiryDate")} required error={errors.expiry}>
                <input
                  type="date"
                  value={form.expiry}
                  onChange={(event) => patchForm("expiry", event.target.value)}
                  className={inputClass(Boolean(errors.expiry))}
                  {...localizedFieldProps()}
                />
              </Field>
            </div>

            {isServiceType && (
              <Field label={sq("complaintId")} required error={errors.complaintId}>
                {complaintSelect}
              </Field>
            )}

            {showServiceRoles ? (
              <>
                <Field label={sq("serviceExecutive")}>
                  <input
                    value={serviceExecutiveLabel}
                    readOnly
                    disabled
                    className={`${inputClass(false)} disabled:cursor-not-allowed disabled:bg-[#f8fbff] disabled:text-[#6f82a1]`}
                    placeholder="—"
                    {...localizedFieldProps()}
                  />
                </Field>
                <Field label={sq("serviceManager")}>
                  <input
                    value={serviceManagerLabel}
                    readOnly
                    disabled
                    className={`${inputClass(false)} disabled:cursor-not-allowed disabled:bg-[#f8fbff] disabled:text-[#6f82a1]`}
                    placeholder="—"
                    {...localizedFieldProps()}
                  />
                </Field>
              </>
            ) : (
              <>
                <Field label={sq("salesExecutive")} error={errors.assignedSalesExecutive}>
                  {salesExecutiveSelect}
                </Field>
                <Field label={sq("salesSupport")} error={errors.assignedSalesSupport}>
                  {salesSupportSelect}
                </Field>
              </>
            )}
          </div>
        )}

        {activeStep === 1 && (
          <div>
            <Field label={sq("customerName")} required error={errors.customer}>
              {customerSelect}
            </Field>
            <Field label={sq("addressOrLocation")} required error={errors.location}>
              {locationSelect}
            </Field>
            <Field label={sq("pointOfContactName")} error={errors.contactName}>
              {contactSelect}
            </Field>
            <Field label={sq("phoneNumber")} error={errors.phone}>
              <input
                type="tel"
                value={form.phone}
                onChange={(event) => patchForm("phone", event.target.value)}
                disabled={!form.customer}
                className={`${inputClass(Boolean(errors.phone))} disabled:cursor-not-allowed disabled:bg-[#f8fbff] disabled:text-[#9aa9c0]`}
                placeholder="+91 98..."
                {...localizedFieldProps()}
              />
            </Field>
            <Field label={sq("email")} error={errors.email}>
              <input
                type="email"
                value={form.email}
                onChange={(event) => patchForm("email", event.target.value)}
                disabled={!form.customer}
                className={`${inputClass(Boolean(errors.email))} disabled:cursor-not-allowed disabled:bg-[#f8fbff] disabled:text-[#9aa9c0]`}
                placeholder="contact@company.com"
                {...localizedFieldProps()}
              />
            </Field>
          </div>
        )}
      </div>

      {/* In-flow footer — never overlays fields */}
      <div className="shrink-0 pt-3">
        <div className="flex items-stretch gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-[12px] border border-[#d0d9ea] bg-white px-4 text-[13px] font-semibold text-[#2944a8]"
          >
            {activeStep > 0 ? sq("back") : i18n.t("formActions.cancel")}
          </button>
          <button
            type="button"
            onClick={handlePrimary}
            disabled={primaryDisabled}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-[12px] bg-[#243c93] px-5 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#a6b3d6]"
          >
            {!isLastStep
              ? sq("mobileNext")
              : submitting
                ? sq("creating")
                : sq("create")}
          </button>
        </div>
      </div>
    </div>
  );
}
